import React, { useEffect, useRef } from "react";
import Layout from "../components/Landing/Layout";
import styles from "./Reviews.module.css";

const Reviews = () => {
  const widgetRef = useRef(null);

  useEffect(() => {
    if (!widgetRef.current) return;

    // ── 1. PURGE BODY ORPHANS ───────────────────────────────────────
    // Third-party scripts like TrustIndex often dump duplicate widgets at the very bottom
    // of document.body during React hot-reloads. We clean those out immediately.
    const purgeBodyOrphans = () => {
      const selectors = [
        ".ti-widget",
        "[class*='ti-widget']",
        "[class*='trustindex']",
        "iframe[src*='trustindex']",
      ];
      document.body.querySelectorAll(selectors.join(",")).forEach((el) => {
        // If element is sitting in document.body outside our component container, delete it
        if (!widgetRef.current?.contains(el)) {
          el.remove();
        }
      });
    };
    purgeBodyOrphans();

    // ── 2. DOM TELEPORTER (MUTATION OBSERVER) ───────────────────────
    // TrustIndex loader scripts automatically call `document.body.appendChild(widget)`
    // ignoring React component trees. This observer intercepts any TrustIndex element
    // dropped below the footer and teleports it directly into our centered Hero wrapper.
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.parentNode === document.body) {
            const el = node;
            const isTI =
              el.classList?.contains("ti-widget") ||
              (typeof el.className === "string" && el.className.includes("ti-")) ||
              el.id?.includes("trustindex") ||
              (el.tagName === "IFRAME" && el.src?.includes("trustindex")) ||
              el.querySelector?.(".ti-widget");

            if (isTI && widgetRef.current) {
              widgetRef.current.appendChild(el);
            }
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: false });

    // ── 3. INJECT SCRIPT ────────────────────────────────────────────
    widgetRef.current.innerHTML = "";
    const script = document.createElement("script");
    script.src = "https://cdn.trustindex.io/loader.js?5a08f88759522100e40675b5beb";
    script.async = true;
    script.defer = true;
    widgetRef.current.appendChild(script);

    // Backup polling check to catch async iframe generation
    const pollInterval = setInterval(() => {
      document.body
        .querySelectorAll(".ti-widget, [class*='ti-widget'], iframe[src*='trustindex']")
        .forEach((el) => {
          if (el.parentNode === document.body && widgetRef.current) {
            widgetRef.current.appendChild(el);
          }
        });
    }, 200);

    const stopPoll = setTimeout(() => clearInterval(pollInterval), 4000);

    // ── CLEANUP ON UNMOUNT ──────────────────────────────────────────
    return () => {
      observer.disconnect();
      clearInterval(pollInterval);
      clearTimeout(stopPoll);
      purgeBodyOrphans();
      if (widgetRef.current) {
        widgetRef.current.innerHTML = "";
      }
    };
  }, []);

  const noop = () => {};

  return (
    <Layout onNavClick={noop} activeSection={null}>
      {/* ─── PREMIUM PAGE HERO ─────────────────────────────────────── */}
      <section className={styles.reviewsHero}>
        <div className={styles.heroContent}>
          <div className={styles.googleRatingBadge}>
            <svg viewBox="0 0 24 24" width="22" height="22" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span className={styles.ratingStars}>★★★★★</span>
            <span className={styles.ratingScore}>4.8 / 5 Average Rating</span>
          </div>

          <h1 className={styles.heroHeadline}>
            Real Stories. <span>Real Recovery.</span>
          </h1>
          <p className={styles.heroSubline}>
            See why patients across Gurugram trust ReformMe Healthcare.
          </p>
        </div>

        {/* Decorative ambient blobs */}
        <div className={styles.ambientBlob1} />
        <div className={styles.ambientBlob2} />
      </section>

      {/* ─── TRUSTINDEX WIDGET CONTAINER ───────────────────────────── */}
      <section className={styles.widgetContainer}>
        <div
          ref={widgetRef}
          id="trustindex-widget-wrapper"
          className="trustindex-widget-container"
          style={{ width: "100%", position: "relative", zIndex: 10, minHeight: "500px" }}
        >
          {/* External TrustIndex reviews mount inside this target element */}
        </div>
      </section>
    </Layout>
  );
};

export default Reviews;
